import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {SearchService} from '../../../../../core/services/search.service';
import {Subscription} from 'rxjs';
import {App, APP_TYPE_PROP_MANAGER, APP_TYPE_TENANT_PORTAL, AppType} from '../../../../../shared/models/app';
import {SortTable, SortTableRow} from '../../../../../shared/models/sort-table/types';
import {
  SortTableCellButtons,
  SortTableCellKebabMenu,
  SortTableCellTextPill,
  SortTableCellTitleSharingInfo,
} from '../../../../../shared/models/sort-table/cell-classes';
import {AppsService} from '../../../../../core/services/apps.service';
import {UrlService} from '../../../../../core/services/url.service';
import {Option} from '../../../../../shared/models/option';
import {SessionService} from '../../../../../core/services/session.service';
import {ShadeToolsService} from '../../../../../core/services/shade-tools.service';
import {BaseTableViewComponent} from '../../../../components/_base-table-view/base-table-view.component';
import {Store} from '@ngrx/store';
import {AppState} from '../../../../../app/app.state';
import {ObjectModel, ObjectType} from '../../../../../shared/models/object.model';
import {SpinnerComponent} from '../../../../components/spinner/spinner.component';
import {SortTableComponent} from '../../../../components/sort-table/sort-table.component';
import { DialogBoxButtonColor } from 'src/shared/models/dialog-box';
import { Permission } from '../../../../../shared/models/fga/permission';
import { PermissionsService } from '../../../../../core/services/permissions.service';

const COL_IDX_KEBAB = 0;
const COL_IDX_NAME = 1;
const COL_IDX_ID = 2;
const COL_IDX_TYPE = 4;
const COL_IDX_BUTTON = 3;

@Component({
  selector: 'motn-apps-existing',
  templateUrl: './apps-existing.component.html',
  styleUrls: ['./apps-existing.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SpinnerComponent,
    SortTableComponent,
  ],
})
export class AppsExistingComponent extends BaseTableViewComponent implements OnInit, OnDestroy {
  private disableShareButtonForAppTypes: AppType[] = [
    APP_TYPE_PROP_MANAGER,
    APP_TYPE_TENANT_PORTAL,
  ];

  apps: App[];
  searchTerm: string = '';

  private appsSub: Subscription;
  private searchSub: Subscription;


  appTypeLabels: { [t: string]: string } = {
    'picture': 'Mosaic',
    'tenant-portal': 'Tenant Portal',
    'prop-manager': 'Property Manager',
  };


  constructor(
    private store: Store<AppState>,
    private appsService: AppsService,
    private searchService: SearchService,
    private urlService: UrlService,
    private shadeTools: ShadeToolsService,
    private session: SessionService,
    private permissionsService: PermissionsService,
  ) {
    super();
  }


  ngOnInit(): void {
    this.searchService.enableSearch();

    this.searchSub = this.urlService.searchTerm$.subscribe(
      (term) => {
        this.searchTerm = term;
        this.updateTable();
      },
    );

    this.appsSub = this.store.select('apps').subscribe(
      (state) => {
        for (let app of state.apps) {
          const row = this.tableRows[app.ID];
          if (row) {
            this.upsertRowData(row, app);
          }
        }

        this.apps = state.apps;
      },
    );

    this.appsService.fetchUserApps$().subscribe(
      (apps) => {
        this.apps = apps;
        this.updateTable();
      },
    );
  }


  ngOnDestroy() {
    this.appsSub?.unsubscribe();
    this.searchSub?.unsubscribe();
  }


  protected updateTable() {
    if (!this.apps) {
      return;
    }

    const colKebabWidth = 40;
    const colIdWidth = 105;
    const colNameWidth = 200;
    const colTypeWidth = 150;
    const colButtonWidth = 120;


    const table: SortTable = {
      css: {
        'min-width': (colKebabWidth + colIdWidth + colNameWidth + colTypeWidth + colButtonWidth) + 'px',
      },
      columns: [],
      rows: [],
      pagination: {
        enabled: true,
        rowsPerPage: 20,
        startOnPage: 1,
        maxPageOptions: 12,
      },
    };

    {
      table.columns[COL_IDX_KEBAB] = {
        name: '',
        css: {
          'width': colKebabWidth + 'px',
          'justify-content': 'center',
        },
      };

      table.columns[COL_IDX_ID] = {
        name: 'ID',
        css: {
          'width': colIdWidth + 'px',
          'justify-content': 'center',
        },
        sortingFn: SortTableCellTextPill.sortingFnPillText,
        sortDefault: 'down',
      };

      table.columns[COL_IDX_NAME] = {
        name: 'Name',
        css: {
          'width': `calc(100% - ${colKebabWidth + colIdWidth + colTypeWidth + colButtonWidth}px)`,
        },
        sortingFn: SortTableCellTitleSharingInfo.sortingFnTitle,
      };

      table.columns[COL_IDX_TYPE] = {
        name: 'Type',
        css: {
          'width': colTypeWidth + 'px',
          'justify-content': 'center',
        },
        sortingFn: SortTableCellTextPill.sortingFnMainText,
      };

      table.columns[COL_IDX_BUTTON] = {
        name: '',
        css: {
          'width': colButtonWidth + 'px',
          'justify-content': 'center',
        },
      };
    }

    let apps: App[] = this.apps;

    if (this.searchTerm) {
      apps = this.searchService.searchApps(this.searchTerm, apps);
    }

    for (let app of apps) {
      let row: SortTableRow = this.tableRows[app.ID];

      if (!row) {
        row = this.tableRows[app.ID] = {
          context: '' + app.ID,
          cells: [],
        };
      }

      this.upsertRowData(row, app);
      table.rows.push(row);
    }

    this.searchService.setResultQty(table.rows.length);
    this.table$.next(table);
  }


  /**
   * For inserting data into a brand new table row or updating an existing
   * table row.
   *
   * @param row
   * @param app
   * @private
   */
  protected upsertRowData(row: SortTableRow, app: App) {
    const href = UrlService.makeAppUrl(app);

    // Some app types' shares should NOT be managed from this view, but
    // rather from within the apps themselves
    if (!this.disableShareButtonForAppTypes.includes(app.Type)) {
      row.cells[COL_IDX_NAME] = new SortTableCellTitleSharingInfo({
        title: app.Name,
        object: new ObjectModel({
          id: app.ID,
          type: ObjectType.APP,
          name: app.Name,
          owner: app.Owner,
        }),
      });
    } else {
      row.cells[COL_IDX_NAME] = new SortTableCellTextPill(app.Name, '');
    }

    // Max 2 options: Open App, Delete
    const kebabCell = new SortTableCellKebabMenu(app.ID, [], app.Name, false, 2);

    // Add click callback that will be called when kebab menu is opened
    kebabCell.addClickCallback(() => {
      this.loadKebabOptions(kebabCell, app, href);
    });
    
    row.cells[COL_IDX_KEBAB] = kebabCell;
    row.cells[COL_IDX_ID] = new SortTableCellTextPill('', '' + app.ID).setPillLetter('a');
    row.cells[COL_IDX_TYPE] = new SortTableCellTextPill(this.appTypeLabels[app.Type], '');
    row.cells[COL_IDX_BUTTON] = new SortTableCellButtons([
      {
        id: 'open',
        text: 'Open App',
        colors: ['indigo'],
        href: href,
      },
    ]);
  }

  /**
   * Load kebab menu options by checking permissions
   */
  private loadKebabOptions(kebabCell: SortTableCellKebabMenu, app: App, href: string): void {
    if (kebabCell.isLoading) return;
    
    kebabCell.isLoading = true;

    this.permissionsService.queryPermissions({
      UserID: this.session.userId,
      ObjectID: app.ID,
      ObjectType: ObjectType.APP,
      Permissions: [Permission.CAN_DELETE],
    }).subscribe({
      next: (response) => {
        const canDelete = response.Results.find(r => r.Permission === Permission.CAN_DELETE)?.Allowed;
        
        const options: Option[] = [
          {
            id: 'open',
            name: 'Open App',
            href: href,
          },
        ];

        if (canDelete) {
          options.push({
            id: 'delete',
            name: 'Delete App',
            callback: () => this.deleteApp(app),
          });
        }

        kebabCell.options = options;
        kebabCell.isLoading = false;
      },
      error: () => {
        kebabCell.isLoading = false;
      },
    });
  }

  /**
   * Handle app deletion with confirmation dialog
   */
  private deleteApp(app: App): void {
    this.shadeTools.startDialog(
      'Delete App',
      `Are you sure you want to delete the app named ${app.Name}? Deleting this app will remove access for all its users and permanently erase its data, but any tiles within it will continue to exist and can be added to other apps.\n\nTo confirm, type DELETE in the box below and confirm.`,
      [
        {
          text: 'Confirm',
          callback: (input: string) => {
            if (input !== 'DELETE') {
              this.shadeTools.errorDialog(
                `The word DELETE must be typed exactly for deletion to occur. Please try again.`,
              );
              return;
            }

            this.appsService.deleteApp$(app.ID).subscribe({
              next: () => {
                this.apps = this.apps.filter(a => a.ID !== app.ID);
                this.updateTable();
                this.shadeTools.closeAll();
              },
              error: () => {
                this.shadeTools.errorDialog();
              }
            });
          },
          color: DialogBoxButtonColor.Red,
        },
        {
          text: 'Cancel',
          callback: () => {
            this.shadeTools.closeAll();
          },
          color: DialogBoxButtonColor.Alt,
        },
      ],
      true,
      false,
      false,
      false,
    );
  }
}
